/**
 * Maneuver-timeseries path construction: group samples by event and sort by time. This is the per-chart
 * O(events × samples) work that used to run synchronously inside `drawTimeSeries` on the main thread as
 * each chart scrolled into view.
 *
 * VMG_PERC gets no special treatment: it draws every sample it is given, like every other channel. (It
 * once had a >150 cap and a 2σ step-outlier "spike" filter; both are gone — do not reintroduce them.)
 *
 * `buildManeuverPaths` is a SINGLE self-contained function (no imports, only JS globals), so the exact
 * same function serves two roles with zero drift risk:
 *   1. the synchronous fallback inside the maneuver TimeSeries components, and
 *   2. the body of a `@nodets` pool task (tasks ship by source and may not reference module scope).
 *
 * The 2σ threshold used by the grouped MIX builder for non-VMG channels uses a hand-inlined copy of
 * d3.deviation's Welford variance (sample, n−1) so the off-thread result is byte-identical to the
 * previous `d3.deviation(...)` behavior; a parity test (`__tests__/unit/maneuverPathsBuild.test.ts`)
 * pins that against the real d3-array.
 */
import { createPool, type Pool } from './parallel/poolManager';

export interface ManeuverPathSample {
  time: number;
  event_id: number;
  [key: string]: unknown;
}
export interface ManeuverPath {
  eventId: number;
  points: ManeuverPathSample[];
  firstPoint: ManeuverPathSample;
}
export interface BuildManeuverPathsPayload {
  collection: ManeuverPathSample[][];
  channel: string;
}

/**
 * Self-contained: references only JS globals (Map/Math/Number/String). Do NOT add imports or
 * outer-scope references to the body — it is shipped by source to a worker. Mirror of the former
 * inline logic in `standard/TimeSeries.tsx` drawTimeSeries (L762-833 pre-refactor).
 */
export function buildManeuverPaths(payload: BuildManeuverPathsPayload): ManeuverPath[] {
  const { collection } = payload;

  const maneuversByEvent = new Map<number, ManeuverPathSample[]>();
  (collection || []).forEach((vals) => {
    (vals || []).forEach((d) => {
      if (!d || !d.event_id) {return;}
      const eventId = d.event_id;
      let cell = maneuversByEvent.get(eventId);
      if (!cell) {
        cell = [];
        maneuversByEvent.set(eventId, cell);
      }
      cell.push(d);
    });
  });
  maneuversByEvent.forEach((points) => points.sort((a, b) => a.time - b.time));

  const out: ManeuverPath[] = [];
  maneuversByEvent.forEach((points, eventId) => {
    if (points.length > 1 && eventId !== null && eventId !== undefined) {
      out.push({ eventId, points: [...points], firstPoint: points[0] });
    }
  });

  return out;
}

// ---------------------------------------------------------------------------------------------------
// Grouped TimeSeries MIX mode: per-individual-maneuver tracks (outlier-filtered for non-VMG channels).
// ---------------------------------------------------------------------------------------------------

export interface GroupedIndividualPath {
  pathKey: string;
  points: ManeuverPathSample[];
  firstPoint: ManeuverPathSample;
}
export interface BuildGroupedIndividualPathsPayload {
  /** Each entry is one individual maneuver's samples. */
  individualCollection: ManeuverPathSample[][];
  channel: string;
  isVmgPercChannel: boolean;
  /** Group key per individualCollection entry, precomputed on the main thread via getGroupKeyFromItem
   * (needs the reactive colour mode). Keys are per-maneuver constant, so deriving them from the first
   * sample matches the former `getGroupKeyFromItem(filteredPoints[0], ...)`. */
  groupKeys: string[];
}

/**
 * Self-contained (JS globals only) — used both as grouped/TimeSeries MIX inline fallback and as a @nodets
 * task. VMG_PERC keeps every sample (no cap, no spike filter, and deliberately NOT the other-channel
 * branch, whose 5s gap break and 2σ step filter it never had); other channels keep the 5s gap break +
 * 2σ step filter, with the 2σ threshold from an inlined d3.deviation (Welford, n−1).
 */
export function buildGroupedIndividualPaths(
  payload: BuildGroupedIndividualPathsPayload
): GroupedIndividualPath[] {
  const { individualCollection, channel, isVmgPercChannel, groupKeys } = payload;
  // VMG_PERC keeps every sample; only the other channels need the step deltas.
  const useStepFilter = !isVmgPercChannel;

  const deltas: number[] = [];
  (useStepFilter ? individualCollection || [] : []).forEach((points) => {
    const sorted = [...points].sort((a, b) => a.time - b.time);
    for (let j = 1; j < sorted.length; j++) {
      const prevY = Number(sorted[j - 1][channel]);
      const currY = Number(sorted[j][channel]);
      if (Number.isFinite(prevY) && Number.isFinite(currY)) {
        deltas.push(Math.abs(currY - prevY));
      }
    }
  });

  // Inlined d3.deviation (Welford, sample n−1) then sqrt — identical to the former d3.deviation(deltas).
  let count = 0;
  let mean = 0;
  let sumSq = 0;
  for (let value of deltas) {
    if (value != null && (value = +value) >= value) {
      const delta = value - mean;
      mean += delta / ++count;
      sumSq += delta * (value - mean);
    }
  }
  const variance = count > 1 ? sumSq / (count - 1) : undefined;
  const dev = variance ? Math.sqrt(variance) : variance;
  const stepStd = deltas.length > 1 ? dev ?? 0 : 0;
  const stepThreshold = stepStd > 0 ? 2 * stepStd : Infinity;

  const filtered: GroupedIndividualPath[] = [];
  (individualCollection || []).forEach((vals, i) => {
    const points = [...vals].sort((a, b) => a.time - b.time);
    const filteredPoints: ManeuverPathSample[] = [];
    let prevPoint: ManeuverPathSample | null = null;
    points.forEach((d) => {
      const yVal = Number(d[channel]);
      if (isVmgPercChannel) {
        filteredPoints.push(d);
        prevPoint = d;
      } else {
        if (prevPoint === null) {
          filteredPoints.push(d);
          prevPoint = d;
        } else {
          const seconds = Math.abs(prevPoint.time - d.time);
          const prevY = Number(prevPoint[channel]);
          const step = Number.isFinite(prevY) && Number.isFinite(yVal) ? Math.abs(yVal - prevY) : 0;
          if (seconds >= 5) return; // gap break: skip sample, keep prevPoint (matches original)
          if (step > stepThreshold) {
            prevPoint = d;
            return;
          }
          filteredPoints.push(d);
          prevPoint = d;
        }
      }
    });
    if (filteredPoints.length > 1) {
      filtered.push({
        pathKey: `individual_${String(groupKeys[i])}_${i}`,
        points: filteredPoints,
        firstPoint: filteredPoints[0],
      });
    }
  });
  return filtered;
}

// Shared, lazily-created pool. Charts draw once when they scroll into view; a persistent pool lets
// several charts' path builds run concurrently across cores without per-chart worker spawn.
let pathsPool: Pool<BuildManeuverPathsPayload, ManeuverPath[]> | null = null;
function getPathsPool(): Pool<BuildManeuverPathsPayload, ManeuverPath[]> {
  if (!pathsPool) {pathsPool = createPool<BuildManeuverPathsPayload, ManeuverPath[]>(buildManeuverPaths);}
  return pathsPool;
}

/** Build a chart's maneuver paths off the main thread (inline fallback when workers are unavailable). */
export function computeManeuverPathsOffThread(
  collection: ManeuverPathSample[][],
  channel: string
): Promise<ManeuverPath[]> {
  return getPathsPool().run({ collection, channel });
}

let groupedPathsPool: Pool<BuildGroupedIndividualPathsPayload, GroupedIndividualPath[]> | null = null;
function getGroupedPathsPool(): Pool<BuildGroupedIndividualPathsPayload, GroupedIndividualPath[]> {
  if (!groupedPathsPool) {
    groupedPathsPool = createPool<BuildGroupedIndividualPathsPayload, GroupedIndividualPath[]>(
      buildGroupedIndividualPaths
    );
  }
  return groupedPathsPool;
}

/** Build grouped MIX individual paths off the main thread (inline fallback when workers unavailable). */
export function computeGroupedIndividualPathsOffThread(
  payload: BuildGroupedIndividualPathsPayload
): Promise<GroupedIndividualPath[]> {
  return getGroupedPathsPool().run(payload);
}
